import { RefObject, useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

/**
 * MIME type for video-only fMP4 with H.264 baseline profile
 * Compatible with Avigilon camera streams
 */
const AVIGILON_VIDEO_MIME_TYPE = 'video/mp4; codecs="avc1.4D400D"'

export interface UseMSEPlayerIProps {
  videoRef: RefObject<HTMLVideoElement>
  url: string | null
  headers: Record<string, string> | undefined
  onError?: (error: Error) => void
  onLoadStart?: () => void
  onCanPlay?: () => void
  onAuthError?: () => void
}

export interface UseMSEPlayerReturnIProps {
  isLoading: boolean
  error: string | null
  abort: () => void
  isStreaming: boolean
}

/**
 * Hook for MSE-based video streaming with authenticated fetch
 */
export const useMSEPlayer = ({
  videoRef,
  url,
  headers,
  onError,
  onLoadStart,
  onCanPlay,
  onAuthError,
}: UseMSEPlayerIProps): UseMSEPlayerReturnIProps => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)

  const mediaSourceRef = useRef<MediaSource | null>(null)
  const sourceBufferRef = useRef<SourceBuffer | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null)
  const chunkCountRef = useRef<number>(0)

  const abort = () => {
    // Cancel the reader first to avoid unhandled promise rejections
    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {
        // Ignore errors during cleanup
      })
      readerRef.current = null
    }

    // Then abort any ongoing fetch request
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort()
      } catch (e) {
        // Ignore errors during cleanup
      }
      abortControllerRef.current = null
    }

    // Clean up MediaSource
    if (mediaSourceRef.current) {
      try {
        if (mediaSourceRef.current.readyState === 'open') {
          mediaSourceRef.current.endOfStream()
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      mediaSourceRef.current = null
    }

    // Clean up SourceBuffer reference
    sourceBufferRef.current = null

    // Clean up video element
    const video = videoRef.current
    if (video && video.src && video.src.startsWith('blob:')) {
      URL.revokeObjectURL(video.src)
      video.src = ''
      video.load()
    }

    setIsStreaming(false)
    setIsLoading(false)
    chunkCountRef.current = 0
  }

  useEffect(() => {
    if (!videoRef.current || !url || !headers) {
      return
    }

    // Check MSE support
    if (!('MediaSource' in window)) {
      const err = new Error(
        'MSE video player: MediaSource API not supported in this browser'
      )
      Sentry.captureException(err)
      setError(err.message)
      onError?.(err)
      return
    }

    const video = videoRef.current
    let mediaSource: MediaSource | null = null
    let sourceBuffer: SourceBuffer | null = null
    let abortController: AbortController | null = null
    let reader: ReadableStreamDefaultReader | null = null

    const cleanup = () => {
      // Cancel reader first to avoid unhandled promise rejections
      if (reader) {
        reader.cancel().catch(() => {
          // Ignore errors during cleanup
        })
      }

      // Then abort fetch
      if (abortController) {
        try {
          abortController.abort()
        } catch (e) {
          // Ignore errors during cleanup
        }
      }

      // Clean up MediaSource
      if (mediaSource) {
        try {
          if (mediaSource.readyState === 'open') {
            mediaSource.endOfStream()
          }
        } catch (e) {
          // Ignore
        }
      }

      // Revoke blob URL
      if (video.src && video.src.startsWith('blob:')) {
        URL.revokeObjectURL(video.src)
      }
    }

    const fetchAndStream = async () => {
      try {
        abortController = new AbortController()
        abortControllerRef.current = abortController

        setIsLoading(true)
        onLoadStart?.()

        const response = await fetch(url, {
          headers,
          mode: 'cors',
          signal: abortController.signal,
        })

        if (!response.ok) {
          // Handle auth errors
          if (response.status === 401 || response.status === 403) {
            const authErr = new Error('Authentication failed')
            setError(authErr.message)
            onAuthError?.()
            return
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        if (!response.body) {
          throw new Error('ReadableStream not supported')
        }

        reader = response.body.getReader()
        readerRef.current = reader

        setIsStreaming(true)

        const appendChunks = async (): Promise<void> => {
          if (!reader || !sourceBuffer || !mediaSource) return

          try {
            const { done: isDone, value } = await reader.read()

            if (isDone) {
              if (mediaSource.readyState === 'open') {
                mediaSource.endOfStream()
              }
              setIsLoading(false)
              return
            }

            // Check if MediaSource is still valid
            if (mediaSource.readyState !== 'open') {
              return
            }

            chunkCountRef.current++

            // Clear loading state after first chunk
            if (chunkCountRef.current === 1) {
              setIsLoading(false)
              onCanPlay?.()
            }

            // Wait if buffer is updating
            if (sourceBuffer.updating) {
              await new Promise<void>((resolve) => {
                sourceBuffer!.addEventListener('updateend', () => resolve(), {
                  once: true,
                })
              })
            }

            // Check again after waiting
            if (mediaSource.readyState !== 'open') {
              return
            }

            try {
              sourceBuffer.appendBuffer(value)
            } catch (err: any) {
              if (err.name === 'QuotaExceededError') {
                // Handle buffer overflow
                await handleQuotaExceeded(sourceBuffer, video)
                // Retry appending this chunk
                sourceBuffer.appendBuffer(value)
              } else {
                throw err
              }
            }

            // Continue reading after append completes
            sourceBuffer.addEventListener(
              'updateend',
              () => {
                appendChunks()
              },
              { once: true }
            )
          } catch (err: any) {
            if (err.name !== 'AbortError') {
              const wrappedErr = new Error(
                `MSE video player: Stream reading error - ${err.message}`
              )
              Sentry.captureException(wrappedErr, {
                extra: { originalError: err },
              })
              setError(err.message)
              onError?.(err)
              setIsLoading(false)
            }
          }
        }

        appendChunks()
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          const wrappedErr = new Error(
            `MSE video player: Fetch and stream error - ${err.message}`
          )
          Sentry.captureException(wrappedErr, {
            extra: { originalError: err },
          })
          setError(err.message)
          onError?.(err)
          setIsLoading(false)
        }
      }
    }

    // Initialize MediaSource
    mediaSource = new MediaSource()
    mediaSourceRef.current = mediaSource
    video.src = URL.createObjectURL(mediaSource)

    const handleSourceOpen = async () => {
      if (!mediaSource) return

      try {
        // Check if codec is supported
        if (!MediaSource.isTypeSupported(AVIGILON_VIDEO_MIME_TYPE)) {
          throw new Error(
            `MIME type ${AVIGILON_VIDEO_MIME_TYPE} not supported. MSE may not work with this video format.`
          )
        }

        sourceBuffer = mediaSource.addSourceBuffer(AVIGILON_VIDEO_MIME_TYPE)
        sourceBufferRef.current = sourceBuffer

        // Handle SourceBuffer errors
        sourceBuffer.addEventListener('error', () => {
          const err = new Error(
            'MSE video player: SourceBuffer error - video format incompatible with MSE'
          )
          Sentry.captureException(err)
          setError(err.message)
          onError?.(err)
        })

        // Start fetching and streaming
        await fetchAndStream()
      } catch (err: any) {
        const wrappedErr = new Error(
          `MSE video player: Source open error - ${err.message}`
        )
        Sentry.captureException(wrappedErr, {
          extra: { originalError: err },
        })
        setError(err.message)
        onError?.(err)
        setIsLoading(false)
      }
    }

    const handleMediaSourceError = () => {
      const err = new Error('MSE video player: MediaSource error')
      Sentry.captureException(err)
      setError(err.message)
      onError?.(err)
      setIsLoading(false)
    }

    mediaSource.addEventListener('sourceopen', handleSourceOpen)
    mediaSource.addEventListener('error', handleMediaSourceError)

    return cleanup
  }, [url, headers, videoRef])

  return {
    isLoading,
    error,
    abort,
    isStreaming,
  }
}

/**
 * Handle QuotaExceededError by removing old buffered data
 */
const handleQuotaExceeded = async (
  sourceBuffer: SourceBuffer,
  video: HTMLVideoElement
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const currentTime = video.currentTime
      const buffered = sourceBuffer.buffered

      if (buffered.length > 0) {
        const start = buffered.start(0)
        const removeEnd = Math.max(start, currentTime - 30)

        if (removeEnd > start) {
          sourceBuffer.addEventListener(
            'updateend',
            () => {
              resolve()
            },
            { once: true }
          )

          sourceBuffer.remove(start, removeEnd)
        } else {
          resolve()
        }
      } else {
        resolve()
      }
    } catch (err) {
      reject(err)
    }
  })
}
